import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import axios from 'axios';
import { getApiUrl } from '../config';

interface LanguageContextType {
  currentLanguage: string;
  toggleLanguage: () => void;
  setLanguage: (language: string) => void;
  t: (key: string) => string;
  detectTextLanguage: (text: string) => Promise<any>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguageContext = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
};

// 첫 페이지 로드 시 백엔드에 한 번 알림 (방문자 세션 통계용 - analytics_middleware가 이 경로를 추적)
// UI 텍스트는 아래 getDefaultTexts가 단일 소스이며, 이 응답의 내용은 사용하지 않습니다.
const pingPageLoad = (language: string) => {
  axios.get(getApiUrl(`language/get-ui-texts?language=${language}`)).catch(() => {});
};

// UI 텍스트 단일 소스 (프론트엔드에서만 관리)
const getDefaultTexts = (language: string) => {
  const texts = {
    ko: {
      // Common
      'common.brother': '형제님',
      'common.sister': '자매님',
      'common.back': '뒤로',
      'common.cancel': '취소',
      'common.confirm': '확인',
      'common.close': '닫기',
      'common.error': '오류',
      'common.loading': '로딩 중...',
      
      // Homepage
      'homepage.title': 'He Listens to Me',
      // Homepage Validation Modal
      'homepage.validation.title': '잠깐만... ',
      'homepage.subtitle': '오늘 하루를 위해 우리 모두를 위해 기도하세요.\n지금 당신의 마음에 있는 이야기를 들려주세요.',
      'homepage.features.title': '나만의 온라인 교회',
      'homepage.features.subtitle': '그 분이 들으십니다',
      'homepage.features.feature1.title': '공동체를 위한 예배',
      'homepage.features.feature1.description': '오늘 우리 국가와 사회가 처한 상황에 대한 예배가 날마다 새로 준비됩니다.',
      'homepage.features.feature2.title': '개인 맞춤 예배',
      'homepage.features.feature2.description': '나만의 기도제목을 입력하시면 나만의 상황을 위로하고 권면하는 예배가 준비됩니다.',
      'homepage.features.feature3.title': '완전한 예배',
      'homepage.features.feature3.description': '성경말씀, 찬송, 설교, 축도까지 완전하게 구성된 예배를 드릴 수 있습니다.',
      'homepage.cta.title': '그 분이 들으십니다',
      'homepage.cta.subtitle': '그 분이 들으십니다',
      'homepage.cta.button': '예배 시작하기',
      'homepage.selectGender': '형제님/자매님만을 위한 예배를 준비합니다.',
      'homepage.genderSelected': '',
      'homepage.prayerPurpose': '하나님과 함께 하는 당신만의 예배 시간입니다',
      'homepage.prayerPlaceholder': '감사한 일, 힘든 일, 바라는 일, 나만의 아픔까지. 지금의 마음을 적어주세요. 언제 어디서든지 오직 나만을 위한 예배를 드리세요. 그 분이 응답하십니다.',
      'homepage.startWorshipButton.ready': '예배를 시작합니다',
      'homepage.startWorshipButton.loading': '오직 당신만을 위한 예배를 준비하고 있습니다...',
      'homepage.characterCount': '/500',
      'homepage.voiceInstructions.start': '음성으로 말씀하셔도 됩니다',
      'homepage.voiceInstructions.stop': '다시 클릭하여 중지',
      'homepage.backToHome': '돌아가기',
      'homepage.privacyNotice.title': '개인정보 보호를 위해 로그인이 없습니다',
      'homepage.privacyNotice.content': '모든 기도 내용은 익명으로 처리되며, 개인을 식별할 수 있는 정보는 수집하지 않습니다.\n형제님/자매님 성별 입력은 예배 서비스 제공 목적으로만 사용되며, 완료 후 안전하게 삭제됩니다.',
      
      // Homepage Alerts
      'homepage.alerts.voiceNotSupported': '음성 인식이 지원되지 않는 브라우저입니다.',
      'homepage.alerts.enterPrayer': '기도 내용을 입력해주세요.',
      'homepage.alerts.selectGender': '성별 구분을 선택해주세요.',
      'homepage.alerts.selectGenderStrict': '형제님/자매님 을 선택해주세요',
      'homepage.alerts.inappropriateContent': '부적절한 내용이 감지되었습니다.',
      'homepage.alerts.ipBlocked': '접근이 차단되었습니다.',
      'homepage.alerts.networkBlocked': '네트워크 접근이 차단되었습니다.',
      'homepage.alerts.networkError': '네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
      'homepage.alerts.prayerTooShort': '기도 내용이 너무 짧습니다. 최소 10자 이상 입력해 주세요.',
      'homepage.alerts.minLength50': '입력 내용의 길이는 50자 이상이어야 합니다',
      'homepage.alerts.dailyWorshipNotAvailable': '선택하신 언어로 오늘의 예배가 아직 준비되지 않았습니다. 잠시 후 다시 시도해주시거나 다른 언어를 선택해주세요.',
      
      // Homepage Dialogs
      'homepage.dialogs.inappropriateContent.title': '부적절한 내용 감지',
      'homepage.dialogs.inappropriateContent.detectedProblem': '감지된 문제:',
      'homepage.dialogs.inappropriateContent.description': '예배는 거룩한 시간입니다. 적절하고 진실한 마음으로 기도해 주시기 바랍니다.',
      'homepage.dialogs.inappropriateContent.understand': '이해했습니다',
      'homepage.dialogs.ipBlocked.title': '접근 차단 알림',
      'homepage.dialogs.ipBlocked.subtitle': '서비스 이용이 제한되었습니다',
      'homepage.dialogs.ipBlocked.emailContact': '차단 해제 문의:',
      'homepage.dialogs.ipBlocked.emailInstructions': '위 이메일로 차단 해제를 요청하시거나 문의사항을 보내주세요',
      'homepage.dialogs.ipBlocked.emailButton': '이메일 문의하기',
      
      // Footer
      'footer.companyName': '회사명:',
      'footer.companyNameValue': '고도화 사회 이니셔티브',
      'footer.representative': '대표자명: 공훈의',
      'footer.businessNumber': '사업자등록번호: 310-29-01213',
      'footer.ecommerceNumber': '통신판매업 신고번호: 2021-서울종로-2147',
      'footer.address': '주소: 서울시 종로구 평창21길 9-26 삼형파크 A동 102호',
      'footer.phone': '유선번호: 070-8621-3108',
      'footer.policies': '정책 안내',
      'footer.terms': '이용약관',
      'footer.privacy': '개인정보처리방침',
      'footer.copyright': '© 2025 Advanced Society Initiative. All rights reserved.',
      
      // DonationPage
      'donation.title': '자발적 헌금',
      'donation.subtitle': '하나님의 은혜에 감사하며 드리는 헌금입니다. 헌금은 명망있는 목사님들로 구성된 운영위원회의 결정에 따라 세계 각지의 청년교회를 위해 쓰여집니다.',
      'donation.amountSelection': '헌금 금액 선택',
      'donation.amountMessage': '마음에 정한 대로 헌금해 주세요',
      'donation.customAmount': '직접 입력',
      'donation.customAmountPlaceholder': '원하는 금액을 입력하세요',
      'donation.selectedAmount': '선택된 금액',
      'donation.payButton': '결제하기',
      'donation.completedTitle': '헌금 완료',
      'donation.completedMessage': '원의 헌금이 완료되었습니다.',
      'donation.returnToWorship': '이전 예배로 돌아가기',
      'donation.returnToHome': '홈으로 돌아가기',
      'donation.regularInfo': '정기헌금 안내',
      'donation.currency': '원',
      'donation.regularDescription': '매월 자동으로 헌금하는 정기헌금 서비스를 이용하실 수 있습니다.',
      'donation.regularBenefitsTitle': '정기헌금의 장점',
      'donation.regularBenefits': '• 매월 자동 결제로 편리함\n• 언제든지 변경/취소 가능\n• 안전한 결제 시스템',
      'donation.setupRegular': '정기헌금 설정하기',
      'donation.securePaymentTitle': '🔒 안전한 결제',
      'donation.securePaymentDescription': '모든 결제 정보는 토스페이먼츠를 통해 암호화되어 안전하게 처리됩니다.\n신용카드 정보는 저장되지 않으며, PCI-DSS 인증을 받은 보안 시스템을 사용합니다.',
      'donation.minAmountError': '최소 결제 금액은 1,000원입니다.',
      'donation.cancel': '취소',
      'donation.returnToWorshipText': '예배로 돌아가기',
      'donation.returnToHomeText': '홈으로 돌아가기',
      'donation.notAcceptingYet': '아직 헌금은 받지 않고 있습니다. 추후 온 세계 청년교회를 위한 헌금 기부를 받을 예정입니다. 관심 주셔서 감사합니다',
      // Eximbay
      'donation.securePaymentDescriptionEximbay': '모든 결제 정보는 엑심베이(Eximbay)를 통해 암호화되어 안전하게 처리됩니다.\n신용카드 정보는 저장되지 않으며, PCI-DSS 인증을 받은 보안 시스템을 사용합니다.',
      'donation.buyerName': '이름 (선택)',
      'donation.buyerEmail': '이메일 (선택, 결제 확인 메일 수신용)',
      'donation.eximbayRedirectNotice': '결제하기를 누르면 엑심베이 안전 결제창으로 이동합니다.',
      'donation.preparingPayment': '결제를 준비하고 있습니다...',
      'donation.paymentInitError': '결제 준비 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
      'donation.paymentNotConfigured': '현재 결제 서비스가 준비 중입니다.',
      'donation.paymentInfo': '결제 정보',
      'donation.amountLabel': '금액',
      
      // WorshipPage
      'worship.praise': '찬송',
      'worship.sermon': '설교',
      'worship.sermonMessage': '설교 말씀',
      'worship.blessing': '축복',
      'worship.blessingTitle': '축도',
      'worship.scripture': '성경말씀',
       'worship.deliveryNotice.prefix': '이번 설교말씀과 축도는',
       'worship.deliveryNotice.suffix': '목사님의 어조와 흐름으로 전해드리겠습니다.',
      'worship.praiseMessage': '찬송하십시다. 아무 키나 누르세요',
      'worship.nextStep': '다음 단계로',
      'worship.loading': '오직 당신만을 위한 예배를 준비하고 있습니다...',
      'worship.preparingTitle': '오직 당신만을 위한 예배를 준비하고 있습니다',
      'worship.personalTitle': '지금 나만을 위한 예배',
      'worship.personalSubtitle': '오직 당신만을 위해 특별히 준비된 예배입니다',
      'worship.dailyTitle': '오늘 우리를 위한 예배',
      'worship.dailySubtitlePrefix': '오늘의 예배 제목 : ',
      'worship.order': '오늘의 예배 순서',
      'worship.worshipMessage': '예배 시작합니다. 아무 키나 누르세요',
      'worship.processing.scripture': '성경 구절 검색 중...',
      'worship.processing.hymn': '찬송가 선곡 중...',
      'worship.processing.sermon': '설교 준비 중...',
      'worship.processing.meditation': '약 1분간 묵상의 시간을 가지세요',
      'worship.processing.analysis': '기도 내용 분석 중...',
      'worship.processing.scriptureHymn': '성경 말씀과 찬송가 찾는 중...',
      'worship.processing.blessing': '축도 준비 중...',
      'worship.processing.finalizing': '예배 순서 정리 중...',
      'worship.thanksGiving': '감사 헌금',
      'worship.thanksGivingMessage': '이 예배가 당신에게 은혜가 되었다면,\n감사한 마음으로 자유롭게 헌금해주세요.',
      'worship.donateButton': '헌금하기',
      'worship.newWorshipButton': '새로운 예배 시작하기',
      'worship.downloadResources': '오늘의 예배 자료 다운로드',
      'worship.sermonDownload': '설교 말씀',
      'worship.blessingDownload': '축도',
      'worship.desktopVideoNotice': '데스크탑 PC에서는 축도를 동영상으로 만들어서 공유할 수 있습니다.',
      'worship.closingMessage': '예배를 마칩니다. 하나님의 은혜가 함께 하시기를 기도합니다.',
      'worship.blessing.stopConfirm.title': '축도를 멈추시겠습니까?',
      'worship.blessing.stopConfirm.message': '축도가 진행 중입니다. 정말로 멈추고 다음 단계로 넘어가시겠습니까?',
      'worship.blessing.stopConfirm.cancel': '계속',
      'worship.blessing.stopConfirm.confirm': '멈추기',
      'worship.donationNotice': '헌금은 최소한의 운영경비를 제외하고 명망있는 목회자님들로 구성된 헌금운영위원회의 결정에 따라 세계 각지의 청년교회를 위해 전액 기부됩니다.',
      'worship.sermon.close': '닫기',
      'worship.sermon.replay': '다시 보기',
      'worship.sermon.viewAll': '모두 보기',
      'worship.pressAnyKey': '아무 키나 눌러서 다음으로 넘어가세요',
      'worship.continueButton': '계속하려면 아무 키나 누르세요',
      'worship.churchInvitation.message': '교회에 나가시면 더 큰 평강과 축복을 얻으실 수 있습니다.\n하나님께서는 모이는 데 힘쓰라고 말씀하셨습니다.',
      'worship.sermonIntroButton': '설교 말씀을 듣겠습니다. 아무 키나 누르세요',
      'worship.hymn.channel': '채널',
      'worship.hymn.duration': '재생시간',
      'worship.hymn.defaultChannel': '찬송가 채널',
      
      // Review Page
      'review.generatingVideo': '동영상 파일 생성 중',
      
      // 기본 축도 성경구절 (백엔드 응답이 없을 때 사용)
      'worship.defaultBlessingReference': '민수기 6:24-26',
      'worship.defaultBlessingVerse': '여호와는 네게 복을 주시고 너를 지키시기를 원하며 여호와는 그의 얼굴을 네게 비치사 은혜 베푸시기를 원하며 여호와는 그 얼굴을 네게로 향하여 드사 평강 주시기를 원하노라',
      
      // TermsPage
      'terms.title': 'He Listens to Me 서비스 이용약관',
      'terms.effectiveDate': '시행일자: 2025년 1월 10일',
      'terms.goHome': '홈으로 가기',
      
      // PrivacyPage
      'privacy.title': 'He Listens to Me 개인정보처리방침',
      'privacy.effectiveDate': '시행일자: 2025년 1월 10일',
      'privacy.goHome': '홈으로 가기',
      
      // PaymentResultPage
      'payment.loading': '결제 결과를 확인하고 있습니다...',
      'payment.success': '결제 완료',
      'payment.failed': '결제 실패',
      'payment.info': '결제 정보',
      'payment.amount': '결제 금액',
      'payment.method': '결제 방법',
      'payment.time': '결제 시간',
      'payment.failedMessage': '결제 처리 중 문제가 발생했습니다.',
      'payment.invalidInfo': '결제 정보가 올바르지 않습니다.',
      'payment.approvalFailed': '결제 승인에 실패했습니다.',
      'payment.processingError': '결제 처리 중 오류가 발생했습니다.',
      'payment.failedDetail': '결제에 실패했습니다.',
      'payment.returnHome': '홈으로 돌아가기',
      'payment.retry': '다시 시도',
      'payment.loginRequired': '로그인이 필요합니다.',
      'payment.pleaseWait': '잠시만 기다려 주세요.',
      'payment.donationSuccess': '헌금이 성공적으로 처리되었습니다.',
      'payment.orderNumber': '주문번호:',
      'payment.returnToWorship': '예배로 돌아가기',
      'payment.customerService': '문의사항이 있으시면 고객센터로 연락해 주세요.',
      'payment.pendingConfirmation': '결제 확인이 아직 완료되지 않았습니다. 잠시 후 다시 확인해 주세요.',
      'payment.canceledByUser': '결제가 취소되었습니다.'
    },
    en: {
      // Common
      'common.brother': 'Brother',
      'common.sister': 'Sister',
      'common.back': 'Back',
      'common.cancel': 'Cancel',
      'common.confirm': 'Confirm',
      'common.close': 'Close',
      'common.error': 'Error',
      'common.loading': 'Loading...',
      
      // Homepage
      'homepage.title': 'He Listens to Me',
      // Homepage Validation Modal
      'homepage.validation.title': 'Hold on a moment...',
      'homepage.subtitle': 'Share the story that\'s in your heart right now.',
      'homepage.features.title': 'My Online Church',
      'homepage.features.subtitle': 'He is listening',
      'homepage.features.feature1.title': 'Worship for Community',
      'homepage.features.feature1.description': 'A new worship service for our nation and society\'s current situation is prepared daily.',
      'homepage.features.feature2.title': 'Personal Worship',
      'homepage.features.feature2.description': 'Enter your prayer requests and a worship service to comfort and encourage your personal situation will be prepared.',
      'homepage.features.feature3.title': 'Complete Worship',
      'homepage.features.feature3.description': 'You can experience a complete worship service with scripture, hymns, sermon, and benediction.',
      'homepage.cta.title': 'Get Started Now',
      'homepage.cta.subtitle': 'He is listening',
      'homepage.cta.button': 'Start Worship',
      'homepage.selectGender': 'We prepare a worship service just for you, Brother/Sister.',
      'homepage.genderSelected': '',
      'homepage.prayerPurpose': 'We will prepare a special worship time with God for you.',
      'homepage.prayerPlaceholder': 'Share your gratitude, hardships, hopes, even your own pain. Write down how you feel right now. Anytime, anywhere, have a worship time made just for you. He answers.',
      'homepage.startWorshipButton.ready': 'Start Worship',
      'homepage.startWorshipButton.loading': 'Preparing worship just for you...',
      'homepage.characterCount': '/500',
      'homepage.voiceInstructions.start': 'You can also speak with your voice',
      'homepage.voiceInstructions.stop': 'Click again to stop',
      'homepage.backToHome': 'Go Back',
      'homepage.privacyNotice.title': 'No login is required for your privacy',
      'homepage.privacyNotice.content': 'All prayer content is processed anonymously, and no personally identifiable information is collected.\nYour gender selection (Brother/Sister) is used only to prepare the worship service and is safely deleted after completion.',
      
      // Homepage Alerts
      'homepage.alerts.voiceNotSupported': 'Voice recognition is not supported in this browser.',
      'homepage.alerts.enterPrayer': 'Please enter your prayer content.',
      'homepage.alerts.selectGender': 'Please select your gender.',
      'homepage.alerts.selectGenderStrict': 'Please select Brother/Sister.',
      'homepage.alerts.inappropriateContent': 'Inappropriate content detected.',
      'homepage.alerts.ipBlocked': 'Access has been blocked.',
      'homepage.alerts.networkBlocked': 'Network access has been blocked.',
      'homepage.alerts.networkError': 'A network error occurred. Please try again later.',
      'homepage.alerts.prayerTooShort': 'Your prayer is too short. Please enter at least 10 characters.',
      'homepage.alerts.minLength50': 'The input must be at least 50 characters.',
      'homepage.alerts.dailyWorshipNotAvailable': 'Today\'s worship is not yet available for this language. Please check back later or try another language.',
      
      // Homepage Dialogs
      'homepage.dialogs.inappropriateContent.title': 'Inappropriate Content Detected',
      'homepage.dialogs.inappropriateContent.detectedProblem': 'Detected Problem:',
      'homepage.dialogs.inappropriateContent.description': 'Worship is a sacred time. Please pray with an appropriate and sincere heart.',
      'homepage.dialogs.inappropriateContent.understand': 'I Understand',
      'homepage.dialogs.ipBlocked.title': 'Access Blocked Notification',
      'homepage.dialogs.ipBlocked.subtitle': 'Service usage has been restricted',
      'homepage.dialogs.ipBlocked.emailContact': 'Unblock Inquiry:',
      'homepage.dialogs.ipBlocked.emailInstructions': 'Please send an unblock request or inquiry to the above email',
      'homepage.dialogs.ipBlocked.emailButton': 'Contact via Email',
      
      // Footer
      'footer.companyName': 'Company:',
      'footer.companyNameValue': 'Advanced Society Initiative',
      'footer.representative': 'Representative: Huney Kong',
      'footer.businessNumber': 'Business Registration: 310-29-01213',
      'footer.ecommerceNumber': 'E-commerce Registration: 2021-Seoul Jongno-2147',
      'footer.address': 'Address: 102, A-dong, Samhyeong Park, 9-26, Pyeongchang 21-gil, Jongno-gu, Seoul',
      'footer.phone': 'Phone: 070-8621-3108',
      'footer.policies': 'Policy Information',
      'footer.terms': 'Terms of Service',
      'footer.privacy': 'Privacy Policy',
      'footer.copyright': '© 2025 Advanced Society Initiative. All rights reserved.',
      
      // DonationPage
      'donation.title': 'Voluntary Donation',
      'donation.subtitle': 'This offering is given in gratitude for God\'s grace. Offerings are used for youth churches around the world according to decisions made by a committee of esteemed pastors.',
      'donation.amountSelection': 'Select Donation Amount',
      'donation.amountMessage': 'Please donate as you have purposed in your heart',
      'donation.customAmount': 'Custom Amount',
      'donation.customAmountPlaceholder': 'Enter desired amount',
      'donation.selectedAmount': 'Selected Amount',
      'donation.payButton': 'Proceed to Payment',
      'donation.completedTitle': 'Donation Completed',
      'donation.completedMessage': 'Your donation has been completed.',
      'donation.returnToWorship': 'Return to Previous Worship',
      'donation.returnToHome': 'Return to Home',
      'donation.regularInfo': 'Regular Giving Information',
      'donation.currency': '',
      'donation.regularDescription': 'Regular donations provide more stable support for our ministry.',
      'donation.regularBenefitsTitle': 'Regular Giving Benefits',
      'donation.regularBenefits': '• Convenient monthly auto-payment\n• Automatic year-end tax receipt\n• Stable ministry support',
      'donation.setupRegular': 'Set Up Regular Giving',
      'donation.securePaymentTitle': '🔒 Secure Payment',
      'donation.securePaymentDescription': 'All payment information is encrypted and processed securely through Toss Payments.\nCredit card information is not stored, and we use a PCI-DSS certified security system.',
      'donation.minAmountError': 'Minimum payment amount is US$ 5.00.',
      'donation.cancel': 'Cancel',
      'donation.returnToWorshipText': 'Return to Worship',
      'donation.returnToHomeText': 'Return to Home',
      'donation.notAcceptingYet': 'We are not accepting donations at this time. We plan to accept donations for youth churches around the world in the future. Thank you for your interest.',
      // Eximbay
      'donation.securePaymentDescriptionEximbay': 'All payment information is encrypted and processed securely through Eximbay.\nCredit card information is not stored, and we use a PCI-DSS certified security system.',
      'donation.buyerName': 'Name (optional)',
      'donation.buyerEmail': 'Email (optional, for payment confirmation)',
      'donation.eximbayRedirectNotice': 'You will be redirected to the Eximbay secure payment page.',
      'donation.preparingPayment': 'Preparing your payment...',
      'donation.paymentInitError': 'Failed to prepare the payment. Please try again later.',
      'donation.paymentNotConfigured': 'The payment service is not available yet.',
      'donation.paymentInfo': 'Payment Details',
      'donation.amountLabel': 'Amount',
      
      // WorshipPage
      'worship.praise': 'Praise',
      'worship.sermon': 'Sermon',
      'worship.sermonMessage': 'Sermon Message',
      'worship.blessing': 'Blessing',
      'worship.blessingTitle': 'Benediction',
      'worship.scripture': 'Scripture',
       'worship.deliveryNotice.prefix': 'The sermon and benediction will be delivered in the tone and flow of',
       'worship.deliveryNotice.suffix': ' Pastor.',
      'worship.praiseMessage': 'Let us praise. Press any key',
      'worship.nextStep': 'Next Step',
      'worship.loading': 'Preparing worship just for you...',
      'worship.preparingTitle': 'Preparing worship just for you',
      'worship.personalTitle': 'A Worship Just For You Now',
      'worship.personalSubtitle': 'A special worship service prepared just for you',
      'worship.dailyTitle': 'Worship for Us Today',
      'worship.dailySubtitlePrefix': 'Today\'s Worship Topic: ',
      'worship.order': 'Today\'s Worship Order',
      'worship.worshipMessage': 'Worship begins. Press any key',
      'worship.processing.scripture': 'Searching for scripture...',
      'worship.processing.hymn': 'Selecting hymns...',
      'worship.processing.sermon': 'Preparing sermon...',
      'worship.processing.meditation': 'Please take about a minute for meditation',
      'worship.processing.analysis': 'Analyzing your prayer...',
      'worship.processing.scriptureHymn': 'Finding scripture and hymns...',
      'worship.processing.blessing': 'Preparing the benediction...',
      'worship.processing.finalizing': 'Arranging the order of worship...',
      'worship.thanksGiving': 'Thanksgiving Offering',
      'worship.thanksGivingMessage': 'If this worship was a blessing to you,\nplease feel free to give an offering with a grateful heart.',
      'worship.donateButton': 'Give Offering',
      'worship.newWorshipButton': 'Start New Worship',
      'worship.downloadResources': 'Download Today\'s Worship Resources',
      'worship.sermonDownload': 'Download Sermon',
      'worship.blessingDownload': 'Download Benediction',
      'worship.desktopVideoNotice': 'On desktop, you can create and share the benediction as a video.',
      'worship.closingMessage': 'We conclude the worship. May God\'s grace be with you.',
      'worship.blessing.stopConfirm.title': 'Stop the benediction?',
      'worship.blessing.stopConfirm.message': 'The benediction is in progress. Do you really want to stop and move to the next step?',
      'worship.blessing.stopConfirm.cancel': 'Continue',
      'worship.blessing.stopConfirm.confirm': 'Stop',
      'worship.donationNotice': 'Donations are distributed in full to youth churches around the world according to decisions made by a donation management committee composed of respected pastors, excluding minimal operating expenses.',
      'worship.sermon.close': 'Close',
      'worship.sermon.replay': 'Replay',
      'worship.sermon.viewAll': 'View All',
      'worship.pressAnyKey': 'Press any key to continue',
      'worship.continueButton': 'Press any key to continue',
      'worship.churchInvitation.message': 'If you attend church, you can receive greater peace and blessings.\nGod has told us to be diligent in gathering together.',
      'worship.sermonIntroButton': 'I will listen to the sermon. Press any key',
      'worship.hymn.channel': 'Channel',
      'worship.hymn.duration': 'Duration',
      'worship.hymn.defaultChannel': 'Hymn Channel',
      
      // Review Page
      'review.generatingVideo': 'Generating video file...',
      
      // Default benediction scripture verses (used when backend response is not available)
      'worship.defaultBlessingReference': 'Numbers 6:24-26',
      'worship.defaultBlessingVerse': 'The Lord bless you and keep you; The Lord make his face shine upon you and be gracious to you; The Lord turn his face toward you and give you peace.',
      
      // TermsPage
      'terms.title': 'He Listens to Me Terms of Service',
      'terms.effectiveDate': 'Effective Date: January 10, 2025',
      'terms.goHome': 'Go Home',
      
      // PrivacyPage
      'privacy.title': 'He Listens to Me Privacy Policy',
      'privacy.effectiveDate': 'Effective Date: January 10, 2025',
      'privacy.goHome': 'Go Home',
      
      // PaymentResultPage
      'payment.loading': 'Checking payment result...',
      'payment.success': 'Payment Completed',
      'payment.failed': 'Payment Failed',
      'payment.info': 'Payment Information',
      'payment.amount': 'Payment Amount',
      'payment.method': 'Payment Method',
      'payment.time': 'Payment Time',
      'payment.failedMessage': 'An issue occurred during payment processing.',
      'payment.invalidInfo': 'Payment information is invalid.',
      'payment.approvalFailed': 'Payment approval failed.',
      'payment.processingError': 'An error occurred during payment processing.',
      'payment.failedDetail': 'Payment failed.',
      'payment.returnHome': 'Return to Home',
      'payment.retry': 'Try Again',
      'payment.loginRequired': 'Login is required.',
      'payment.pleaseWait': 'Please wait a moment.',
      'payment.donationSuccess': 'Donation has been processed successfully.',
      'payment.orderNumber': 'Order ID:',
      'payment.returnToWorship': 'Return to Worship',
      'payment.customerService': 'If you have any questions, please contact our customer service.',
      'payment.pendingConfirmation': 'Payment confirmation is still in progress. Please check again shortly.',
      'payment.canceledByUser': 'The payment was canceled.'
    }
  };
  
  return texts[language as keyof typeof texts] || texts.ko;
};

// 초기 언어를 localStorage에서 동기적으로 읽어서 설정
const getInitialLanguage = (): string => {
  try {
    const savedLang = localStorage.getItem('preferredLanguage');
    if (savedLang && (savedLang === 'ko' || savedLang === 'en')) {
      return savedLang;
    }
  } catch (error) {
    console.error('localStorage 읽기 오류:', error);
  }
  
  // localStorage가 없거나 유효하지 않으면 브라우저 언어 감지
  const browserLang = navigator.language.toLowerCase();
  const detectedLang = browserLang.startsWith('ko') ? 'ko' : 'en';
  
  // localStorage에 저장
  try {
    localStorage.setItem('preferredLanguage', detectedLang);
  } catch (error) {
    console.error('localStorage 저장 오류:', error);
  }
  
  return detectedLang;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initialLanguage = getInitialLanguage();
  const [currentLanguage, setCurrentLanguage] = useState<string>(initialLanguage);
  const [isLanguageChanging, setIsLanguageChanging] = useState<boolean>(false);
  
  // 기본 텍스트를 즉시 로드
  const [uiTexts, setUiTexts] = useState<any>(() => {
    const defaultTexts = getDefaultTexts(initialLanguage);
    return defaultTexts || {};
  });
  
  // 이전 언어의 텍스트를 백업으로 유지
  const [previousTexts, setPreviousTexts] = useState<any>(() => {
    const defaultTexts = getDefaultTexts(initialLanguage);
    return defaultTexts || {};
  });

  // 첫 로드 시 한 번만 방문자 세션 통계용 핑 (텍스트는 getDefaultTexts가 단일 소스)
  useEffect(() => {
    pingPageLoad(initialLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLanguage = useCallback(() => {
    const newLang = currentLanguage === 'ko' ? 'en' : 'ko';
    console.log('🔄 언어 토글 시작:', currentLanguage, '->', newLang);
    
    // localStorage에 저장
    localStorage.setItem('preferredLanguage', newLang);
    
    // 언어 변경 시작 - 이전 텍스트 백업하고 새 텍스트로 즉시 교체
    flushSync(() => {
      // 이전 텍스트 백업
      setPreviousTexts({ ...uiTexts });
      setIsLanguageChanging(true);
      console.log('💾 이전 텍스트 백업 완료');
      
      // 새 언어의 기본 텍스트로 즉시 교체
      const newDefaultTexts = getDefaultTexts(newLang);
      setUiTexts(newDefaultTexts || {});
      setCurrentLanguage(newLang);
      console.log('📚 새 텍스트 로드 완료:', Object.keys(newDefaultTexts || {}));
    });
    
    // 잠시 후 언어 변경 완료
    setTimeout(() => {
      setIsLanguageChanging(false);
      console.log('✅ 언어 토글 완료');
    }, 100);
  }, [currentLanguage, uiTexts]);

  const setLanguage = useCallback((targetLanguage: string) => {
    if (targetLanguage === currentLanguage) {
      console.log('🔄 이미 같은 언어입니다:', targetLanguage);
      return;
    }
    
    console.log('🔄 언어 설정 시작:', currentLanguage, '->', targetLanguage);
    
    // localStorage에 저장
    localStorage.setItem('preferredLanguage', targetLanguage);
    
    // 언어 변경 시작 - 이전 텍스트 백업하고 새 텍스트로 즉시 교체
    flushSync(() => {
      // 이전 텍스트 백업
      setPreviousTexts({ ...uiTexts });
      setIsLanguageChanging(true);
      console.log('💾 이전 텍스트 백업 완료');
      
      // 새 언어의 기본 텍스트로 즉시 교체
      const newDefaultTexts = getDefaultTexts(targetLanguage);
      setUiTexts(newDefaultTexts || {});
      setCurrentLanguage(targetLanguage);
      console.log('📚 새 텍스트 로드 완료:', Object.keys(newDefaultTexts || {}));
    });
    
    // 잠시 후 언어 변경 완료
    setTimeout(() => {
      setIsLanguageChanging(false);
      console.log('✅ 언어 설정 완료');
    }, 100);
  }, [currentLanguage, uiTexts]);

  // 텍스트 언어 감지 함수
  const detectTextLanguage = useCallback(async (text: string) => {
    try {
      if (!text.trim()) return null;

      const response = await axios.post(getApiUrl('language/detect-language'), {
        text: text
      });

      if (response.data.success) {
        const languageInfo = {
          detected_language: response.data.detected_language,
          language_name: response.data.language_name,
          confidence: response.data.confidence,
          analysis: response.data.analysis,
          mixed_languages: response.data.mixed_languages
        };
        return languageInfo;
      }
      return null;
    } catch (error) {
      console.error('언어 감지 중 오류:', error);
      return null;
    }
  }, []);

  // 현재 언어의 기본 텍스트를 메모이제이션
  const defaultTexts = useMemo(() => getDefaultTexts(currentLanguage), [currentLanguage]);
  
  // 번역 함수 (평면적 키 구조 지원)
  const t = useCallback((key: string): string => {
    // 1. 현재 uiTexts에서 찾기 (평면적 키)
    if (uiTexts && Object.keys(uiTexts).length > 0) {
      if (key in uiTexts && typeof (uiTexts as any)[key] === 'string') {
        return (uiTexts as any)[key];
      }
    }
    
    // 2. 언어 변경 중이면 이전 텍스트에서 찾기
    if (isLanguageChanging && previousTexts && Object.keys(previousTexts).length > 0) {
      if (key in previousTexts && typeof (previousTexts as any)[key] === 'string') {
        console.log('🔄 언어 변경 중 이전 텍스트 사용:', key, '=', (previousTexts as any)[key]);
        return (previousTexts as any)[key];
      }
    }
    
    // 3. 기본 텍스트에서 찾기 (평면적 키)
    if (defaultTexts && key in defaultTexts && typeof (defaultTexts as any)[key] === 'string') {
      return (defaultTexts as any)[key];
    }
    
    // 4. 마지막 수단: 이전 텍스트에서 찾기
    if (previousTexts && Object.keys(previousTexts).length > 0) {
      if (key in previousTexts && typeof (previousTexts as any)[key] === 'string') {
        console.log('🆘 마지막 수단으로 이전 텍스트 사용:', key, '=', (previousTexts as any)[key]);
        return (previousTexts as any)[key];
      }
    }
    
    // 5. 정말 찾을 수 없으면 키 자체 반환
    console.warn('❌ 키를 찾을 수 없음:', key);
    return key;
  }, [uiTexts, defaultTexts, isLanguageChanging, previousTexts]);

  // Context value 메모이제이션으로 불필요한 리렌더링 방지
  const contextValue = useMemo(() => ({
    currentLanguage,
    toggleLanguage,
    setLanguage,
    t,
    detectTextLanguage
  }), [currentLanguage, toggleLanguage, setLanguage, t, detectTextLanguage]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};